import { useQueryClient } from "@tanstack/react-query"
import type { AgentActionContent, MembershipRole } from "@zplanem/responses"
import type { Translations } from "@zplanem/translations"
import { useState } from "react"
import { useUser } from "../../app/user-context"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { cn } from "../../lib/utils"

const MEMBERSHIP_ROLES = ["member", "contractor", "architect"] as const
function isMembershipRole(s: string): s is MembershipRole {
	return (MEMBERSHIP_ROLES as readonly string[]).includes(s)
}

function cardTitle(t: Translations, toolName: string): string {
	const titles = t.mareczek.agentActions.card.titles
	return toolName in titles
		? titles[toolName as keyof typeof titles]
		: titles.generic
}

/**
 * The approval card for a Mareczek-proposed mutation (ZPL-122/123): states
 * the action plainly from `displayParams`, offers Approve/Decline while
 * pending, and collapses to a status line once resolved. The resolved state
 * is server-persisted (the bubble's content is updated on resolve), so a
 * reopened chat renders the final state without this component's local
 * memory.
 */
export function AgentActionCard({
	action,
	propertyId,
	chatId,
	t,
}: {
	action: AgentActionContent
	propertyId: string
	chatId: string
	t: Translations
}) {
	const queryClient = useQueryClient()
	const { profile } = useUser()
	// Owner gating (ZPL-295): only the action's owner may act — the server
	// 404s everyone else, so rendering the buttons to other shared-chat
	// members was actionable-looking noise that could only error. Cards
	// posted before `userId` shipped lack it — treat those as actionable so
	// a pending pre-deploy card doesn't lose its buttons for its owner.
	const canAct = action.userId === undefined || action.userId === profile?.id
	const [submitting, setSubmitting] = useState<"accept" | "decline" | null>(
		null,
	)
	// Local override so the buttons collapse immediately on success; the
	// refetch (triggered by the outcome message's realtime event and our
	// invalidation) reconciles with the server-persisted bubble state.
	const [localStatus, setLocalStatus] = useState<
		AgentActionContent["status"] | null
	>(null)
	const [error, setError] = useState<string | null>(null)

	const status = localStatus ?? action.status

	async function resolve(verb: "accept" | "decline") {
		setSubmitting(verb)
		setError(null)
		try {
			await apiFetch(`/api/common/agent-actions/${action.actionId}/${verb}`, {
				method: "POST",
			})
			setLocalStatus(verb === "accept" ? "accepted" : "declined")
		} catch (err) {
			if (err instanceof ApiError) {
				// 410 — the action expired between render and click; the server
				// already flipped the bubble. 409 — resolved elsewhere. Both
				// reconcile via the refetch below; surface the localized message.
				setError(err.message)
				if (err.status === 410) setLocalStatus("expired")
			} else {
				setError(t.mareczek.agentActions.failed)
			}
		} finally {
			setSubmitting(null)
			await queryClient.invalidateQueries({
				queryKey: queryKeys.chatMessages(propertyId, chatId),
			})
		}
	}

	const detail = Object.entries(action.displayParams)
		.map(([key, value]) =>
			key === "role" && isMembershipRole(value)
				? t.properties.share.roles[value]
				: value,
		)
		.join(" · ")

	const statusLabel =
		status === "accepted"
			? t.mareczek.agentActions.card.statusAccepted
			: status === "declined"
				? t.mareczek.agentActions.card.statusDeclined
				: status === "expired"
					? t.mareczek.agentActions.card.statusExpired
					: null

	return (
		<div className="flex flex-col gap-1.5">
			<div className="text-sm font-medium">{cardTitle(t, action.toolName)}</div>
			{detail.length > 0 && (
				<div className="text-sm [overflow-wrap:anywhere]">{detail}</div>
			)}
			{status === "pending" && canAct ? (
				<div className="flex gap-2 pt-0.5">
					<button
						type="button"
						disabled={submitting !== null}
						onClick={() => resolve("accept")}
						className={cn(
							// whitespace-nowrap: the bubble sets overflow-wrap:anywhere
							// (inherited) — a CTA label must never break mid-word.
							"px-3 py-1 rounded-md text-sm font-medium whitespace-nowrap text-primary-foreground bg-primary hover:brightness-95",
							"disabled:opacity-50 disabled:pointer-events-none",
						)}
					>
						{t.mareczek.agentActions.card.accept}
					</button>
					<button
						type="button"
						disabled={submitting !== null}
						onClick={() => resolve("decline")}
						className={cn(
							"px-3 py-1 rounded-md text-sm font-medium whitespace-nowrap border border-border text-foreground hover:bg-muted",
							"disabled:opacity-50 disabled:pointer-events-none",
						)}
					>
						{t.mareczek.agentActions.card.decline}
					</button>
				</div>
			) : status === "pending" ? (
				<div className="text-xs text-muted-foreground">
					{t.mareczek.agentActions.card.statusAwaitingOwner}
				</div>
			) : (
				statusLabel && (
					<div
						className={cn(
							"text-xs",
							status === "accepted"
								? "text-green-600 dark:text-green-500"
								: "text-muted-foreground",
						)}
					>
						{status === "accepted" ? `✓ ${statusLabel}` : statusLabel}
					</div>
				)
			)}
			{error && <div className="text-xs text-destructive">{error}</div>}
		</div>
	)
}
